/**
 * «Recordar» en una fila de Cobros pendientes — `OD-0062` E-04 (ACCEPTED
 * 2026-10-25).
 *
 * Prepara el recordatorio en el correo o el WhatsApp DEL USUARIO, como el botón
 * «Enviar» de los documentos (`enlaces/redactar-correo.ts`): Faktelio no manda
 * nada, no hay cron ni coste. El mensaje lleva el número, lo pendiente y los
 * días, tal como los da la lista: aquí no se calcula nada.
 *
 * Al abrirlo queda una actividad hecha, «Correo: recordatorio de F…», enlazada
 * a la factura. Sin CRM en el plan (402) o sin permiso (403) no queda, y el
 * recordatorio se prepara igual. Los recordatorios AUTOMÁTICOS
 * (`REQ-PROF-AR-006`) siguen fuera.
 */
import { useEffect, useState } from "react";

import { api, type FacturaPendiente } from "../api/cliente";
import { enlaceRedactar, enlaceWhatsApp, telefonoWhatsApp, type ServicioCorreo } from "../enlaces/redactar-correo";
import { t } from "../i18n/es";
import { diaLegible, importeLegible } from "../ui/formato";
import { Aviso, Boton, Campo } from "../ui/primitivas";

type Contacto = { readonly email: string; readonly telefono: string | null; readonly pais: string; readonly emisor: string };

/** El asunto y el texto, con lo que dice la fila. */
export function mensajeDeRecordatorio(f: FacturaPendiente, emisor: string): { readonly asunto: string; readonly cuerpo: string } {
  const numero = f.number ?? "";
  const situacion =
    f.daysOverdue !== null && f.daysOverdue > 0
      ? t.pendientes.recordar.vencida(f.daysOverdue)
      : f.dueDate !== null
        ? t.pendientes.recordar.vence(diaLegible(f.dueDate))
        : f.issueDate !== null
          ? t.pendientes.recordar.emitida(diaLegible(f.issueDate))
          : "";
  return {
    asunto: t.pendientes.recordar.asunto(numero, emisor),
    cuerpo: t.pendientes.recordar.cuerpo(numero, importeLegible(f.pending), situacion, emisor),
  };
}

export function RecordarCobro({
  factura,
  onCerrar,
  onSesionCaducada,
}: {
  readonly factura: FacturaPendiente;
  readonly onCerrar: () => void;
  readonly onSesionCaducada: () => void;
}) {
  const [contacto, setContacto] = useState<Contacto | null>(null);
  const [para, setPara] = useState("");
  // La actividad del recordatorio: una por panel abierto, aunque se pulsen varios botones.
  const [actividad, setActividad] = useState<"nada" | "guardando" | "guardada" | "sin-crm">("nada");

  // El email y el teléfono de su ficha, y el nombre con el que firma el usuario.
  useEffect(() => {
    let vigente = true;
    void Promise.all([api.obtenerCliente(factura.customerId), api.obtenerEmisor()]).then(([c, e]) => {
      if (!vigente) return;
      if (!c.ok && c.estado === 401) return onSesionCaducada();
      const cliente = c.ok ? c.datos : null;
      setPara(cliente?.email ?? "");
      setContacto({
        email: cliente?.email ?? "",
        telefono: cliente?.phone ?? null,
        pais: cliente?.country ?? "ES",
        emisor: e.ok ? (e.datos.emisor?.legalName ?? "") : "",
      });
    });
    return () => {
      vigente = false;
    };
  }, [factura.customerId, onSesionCaducada]);

  if (contacto === null) return <p className="ayuda-neutra">{t.pendientes.recordar.preparando}</p>;

  const { asunto, cuerpo } = mensajeDeRecordatorio(factura, contacto.emisor);
  const whatsapp = telefonoWhatsApp(contacto.telefono, contacto.pais);

  async function apuntar(): Promise<void> {
    if (actividad !== "nada" || factura.number === null) return;
    setActividad("guardando");
    // Lo que no sea guardarla (sin CRM, sin permiso, sin red) no impide recordar.
    const r = await api.crearActividad(
      {
        kind: "EMAIL",
        title: t.pendientes.recordar.actividad(factura.number),
        notes: null,
        dueDate: null,
        dueTime: null,
        important: false,
        customerId: null,
        opportunityId: null,
        quoteId: null,
        invoiceId: factura.invoiceId,
        repeat: null,
      },
      true,
    );
    if (r.ok) return setActividad("guardada");
    if (r.estado === 401) return onSesionCaducada();
    // Sin CRM o sin permiso no se vuelve a intentar; un fallo de red, sí.
    setActividad(r.estado === 402 || r.estado === 403 ? "sin-crm" : "nada");
  }

  function abrir(servicio: ServicioCorreo | "whatsapp"): void {
    void apuntar();
    const url = servicio === "whatsapp" ? enlaceWhatsApp(whatsapp, cuerpo) : enlaceRedactar(servicio, { para, asunto, cuerpo });
    if (servicio === "local") window.location.href = url;
    else window.open(url, "_blank", "noopener,noreferrer");
  }

  return (
    <div className="pendientes-recordar">
      <p className="ayuda-neutra">{t.pendientes.recordar.detalle}</p>
      <Campo
        id={`recordar-para-${factura.invoiceId}`}
        etiqueta={t.enviarDocumento.para}
        valor={para}
        onChange={setPara}
        tipo="email"
        inputMode="email"
        autoComplete="off"
        requerido={false}
        ayuda={t.enviarDocumento.ayudaPara}
      />
      <div className="fila-botones">
        <Boton variante="secundario" onClick={() => abrir("gmail")}>
          {t.enviarDocumento.gmail}
        </Boton>
        <Boton variante="secundario" onClick={() => abrir("outlook")}>
          {t.enviarDocumento.outlook}
        </Boton>
        <Boton variante="secundario" onClick={() => abrir("local")}>
          {t.enviarDocumento.local}
        </Boton>
        <Boton variante="secundario" onClick={() => abrir("whatsapp")}>
          {t.enviarDocumento.whatsapp}
        </Boton>
      </div>
      <p className="ayuda-neutra">
        {whatsapp === "" ? t.pendientes.recordar.whatsappSinNumero : t.pendientes.recordar.whatsappAl(`+${whatsapp}`)}
      </p>
      {actividad === "guardada" ? <Aviso tono="info">{t.pendientes.recordar.apuntado}</Aviso> : null}
      <Boton variante="terciario" onClick={onCerrar}>
        {t.pendientes.recordar.cerrar}
      </Boton>
    </div>
  );
}
